import * as React from 'react';
import { observer } from 'mobx-react';
import { Navigate } from 'react-router-dom';
import { StoreContext } from 'Models/Store';
import { Permission } from 'Models/Permissions';

export interface ISecuredPageProps {
	/**
	 * The groups that can access this page.
	 * If this is undefined then the page will be available to all
	 */
	groups?: string[];
	/**
	 * The capability that can access this page, for screens whose endpoints are gated on one.
	 *
	 * Prefer this over {@link groups} where the endpoints behind the page carry
	 * `[RequirePermission]`: a group list cannot see a capability granted to one person, so the page
	 * would turn away someone every request behind it would have accepted.
	 *
	 * Checked instead of `groups`, not as well — a capability already carries its own tier floor on
	 * the server, and ANDing a group list on top would mean a grant could never open the page. That
	 * is the same trap `[RequirePermission]` has to avoid beside `[Authorize]`.
	 */
	permission?: Permission;
	/** Children elements */
	children: React.ReactNode;
	redirectTo?: string;
}

class SecuredPage extends React.Component<ISecuredPageProps> {
	static contextType = StoreContext;

	public render() {
		const { groups, permission, children, redirectTo = '/404' } = this.props;
		const context = this.context as React.ContextType<typeof StoreContext>;

		if (permission) {
			return context?.can(permission) ? children : <Navigate to={redirectTo} replace />;
		}

		if (groups) {
			if (!groups.length) {
				return <Navigate to={redirectTo} replace />;
			}

			// eslint-disable-next-line react/destructuring-assignment
			if (groups.some(r => context?.userGroups.map(ug => ug.name).includes(r))) {
				return children;
			}

			return <Navigate to={redirectTo} replace />;
		}

		return children;
	}
}

export default observer(SecuredPage);
